// ─────────────────────────────────────────────────────────────────────────────
// WP-A6 / RF-066 — armado del "Reporte de puntos" (Admin › Brillamos › Puntos).
//
// El reporte salía con los IDENTIFICADORES crudos en las columnas Persona y Unidad organizativa:
// tres GUID y un número. RulesEngine no puede resolver esos nombres —el directorio y el catálogo de
// OUs viven en UserAdminService, y la regla de oro #2 le prohíbe leer su base—, así que la
// traducción se hace aquí, donde la pantalla ya tiene el catálogo de OUs cargado y el directorio a
// una llamada de distancia.
//
// Vive fuera del componente para poder probarse sin montar `AdminPoints`, que arrastra el bucle de
// render documentado en su propia suite.
// ─────────────────────────────────────────────────────────────────────────────
import type { PointsExportRowDto } from '@/api/recognition';

/**
 * Identificadores por petición al directorio. El reporte puede traer miles de filas y el lote viaja
 * en el cuerpo, así que se trocea en vez de mandar un único cuerpo enorme que el servidor pueda
 * rechazar entero. Es el mismo tope que `POST v1/internal/users/employee-ids` documenta como
 * "decenas de identidades por página"; `batch-lookup` no declara el suyo, y asumir que no lo tiene
 * sería apoyarse en una ausencia.
 */
export const LOTE_DE_NOMBRES = 200;

/** Nombres ya resueltos con los que se rellena el reporte. */
export interface FuentesDeNombres {
  nombrePorPersona: ReadonlyMap<string, string>;
  nombrePorOu: ReadonlyMap<string, string>;
}

/** Encabezados traducidos de las tres columnas. */
export interface EtiquetasDelReporte {
  persona: string;
  unidad: string;
  puntos: string;
}

/** Lo que devuelve una resolución de nombres: lo logrado, y si quedó algo sin lograr. */
export interface NombresResueltos {
  nombres: ReadonlyMap<string, string>;

  /**
   * `false` si alguna consulta al directorio falló. La pantalla lo convierte en un aviso visible:
   * el reporte sale igual —con identificadores en las filas que no se pudieron traducir— pero el
   * administrador tiene que enterarse de que está leyendo un reporte a medias.
   */
  completo: boolean;
}

/**
 * Traduce las filas del backend a las del Excel. Un identificador que no se pudo resolver se
 * escribe TAL CUAL en vez de dejarse en blanco: el reporte lo usa un comité para decidir premios, y
 * una fila anónima con puntos es peor que una fila fea.
 */
export function construirFilasReportePuntos(
  filas: readonly PointsExportRowDto[],
  fuentes: FuentesDeNombres,
  etiquetas: EtiquetasDelReporte,
): Array<Record<string, string | number>> {
  return filas.map((fila) => ({
    [etiquetas.persona]: fuentes.nombrePorPersona.get(fila.personaId) ?? fila.personaId,
    [etiquetas.unidad]: fila.ouId ? fuentes.nombrePorOu.get(fila.ouId) ?? fila.ouId : '',
    [etiquetas.puntos]: fila.puntos,
  }));
}

/**
 * Resuelve los nombres de las personas del reporte contra el directorio, en lotes y sin repetir
 * identificadores.
 *
 * **No lanza.** Una consulta fallida deja sin traducir a las personas de ESE lote y marca
 * `completo: false`; las demás se conservan. Tirar la descarga entera porque el directorio parpadeó
 * le quitaría al administrador un reporte que ya es utilizable con identificadores.
 */
export async function resolverNombresDePersonas(
  personaIds: readonly string[],
  consultarDirectorio: (ids: string[]) => Promise<Array<{ id: string; displayName: string }>>,
): Promise<NombresResueltos> {
  const unicos = [...new Set(personaIds.filter((personaId) => personaId))];
  const nombres = new Map<string, string>();
  let completo = true;

  for (let inicio = 0; inicio < unicos.length; inicio += LOTE_DE_NOMBRES) {
    const lote = unicos.slice(inicio, inicio + LOTE_DE_NOMBRES);
    try {
      const encontrados = await consultarDirectorio(lote);
      for (const persona of encontrados) {
        if (persona.displayName) nombres.set(persona.id, persona.displayName);
      }
    } catch (error) {
      // No se silencia: se propaga al llamador como `completo: false`, que la pantalla pinta como
      // aviso. El `console.error` solo existe para depurar en desarrollo.
      completo = false;
      if (import.meta.env.DEV) {
        console.error('No se pudieron resolver nombres del reporte de puntos', error);
      }
    }
  }

  return { nombres, completo };
}
